import { useEffect, useMemo, type FC, type PropsWithChildren } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useBasePath } from "../hooks/router";
import { useTRPC } from "../trpc";
import { FullPageLoader } from "../components/FullPageLoader";
import { FullPageError } from "../components/FullPageError";
import { useIntl } from "react-intl";
import { useSessionAccountName, useSetUser, useUser } from "../hooks/user";
import { useSetLocale } from "../hooks/locale";
import { useQuery } from "@tanstack/react-query";
import type { Locale } from "@project/common";
import { AppShell } from "./components/AppShell";
import type { NavMenuOptions } from "../types/menu";
import { MenuType } from "../types/menu";
import { IconHome } from "@tabler/icons-react";

export const ContactLayout: FC<PropsWithChildren> = ({ children }) => {
  const user = useUser();
  const setUser = useSetUser();
  const navigate = useNavigate();
  const setLocale = useSetLocale();
  const intl = useIntl();
  const pathname = useBasePath();
  const trpc = useTRPC();
  const currentAccountName = useSessionAccountName();

  const { data, isLoading, isError, error } = useQuery(trpc.getCurrentUser.queryOptions(undefined, {
    enabled: !user,
  }));

  useEffect(() => {
    if (data) {
      if (data.locale !== intl.locale) {
        setLocale(data.locale as Locale);
      }
    }
  }, [data, intl, setLocale]);

  useEffect(() => {
    if (data) {
      setUser(data);

      if (data.currentAccount?.isServiceAccount) {
        return;
      }
      if (!data.currentProvider.emailVerified) {
        void navigate({ to: `${pathname}/email-verification` });
      }
      else if (!data.currentAccount) {
        void navigate({ to: `${pathname}/select-account` });
      }
      else if (data.currentAccount.isPaymentRequired) {
        void navigate({ to: `${pathname}/subscription/payment` });
      }
    }
  }, [data, setUser, navigate, pathname]);

  const menu: NavMenuOptions = useMemo(() => ({
    items: [
      {
        title: intl.formatMessage({
          defaultMessage: "Overview",
          id: "navigation.overview",
        }),
        type: MenuType.Single,
        Icon: IconHome,
        to: "/$accountName",
        params: { accountName: currentAccountName },
      },
    ],
  }), [currentAccountName, intl]);

  if (isLoading) {
    return <FullPageLoader />;
  }
  if (isError) {
    return <FullPageError error={error} />;
  }
  if (!user) {
    return <FullPageLoader />;
  }

  return (
    <AppShell menu={menu}>
      {children}
    </AppShell>
  );
};
